import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../../components/Pagination';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Badge,
  Stack
} from '@mui/material';
import { FileDownload, Logout, Visibility, Edit, Delete, CheckCircle, Cancel, Email, People, Event, BarChart, Article, AccountBalanceWallet, School, InsertDriveFile } from '@mui/icons-material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import api from '../../services/api';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openRegistrationDialog, setOpenRegistrationDialog] = useState(false);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState('');
  const [registrationsPage, setRegistrationsPage] = useState(1);
  const [contactsPage, setContactsPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [registrationsResponse, contactsResponse] = await Promise.all([
          api.getRegistrations(),
          api.getContacts()
        ]);
        
        setRegistrations(registrationsResponse.data.registrations);
        setContacts(contactsResponse.data.contacts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleExport = async () => {
    try {
      const response = await api.exportRegistrations();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'registrations.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting registrations:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewRegistration = (registration) => {
    setSelectedRegistration(registration);
    setStatusToUpdate(registration.status);
    setOpenRegistrationDialog(true);
  };
  
  const handleDownloadFile = (fileUrl, fileType) => {
    try {
      // Construire l'URL complète si nécessaire
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3002';
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${baseUrl}${fileUrl}`;
      
      // Ouvrir le fichier dans un nouvel onglet (plus fiable que le téléchargement direct)
      window.open(fullUrl, '_blank');
      
      console.log(`Ouverture du fichier ${fileType}:`, fullUrl);
    } catch (error) {
      console.error(`Erreur lors de l'ouverture du fichier ${fileType}:`, error);
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setOpenContactDialog(true);
    
    // Marquer le contact comme lu s'il ne l'est pas déjà
    if (!contact.isRead) {
      markContactAsRead(contact._id);
    }
  };

  const handleCloseRegistrationDialog = () => {
    setOpenRegistrationDialog(false);
    setSelectedRegistration(null);
  };

  const handleCloseContactDialog = () => {
    setOpenContactDialog(false);
    setSelectedContact(null);
  };

  const handleStatusChange = (event) => {
    setStatusToUpdate(event.target.value);
  };

  const updateRegistrationStatus = async () => {
    try {
      await api.updateRegistrationStatus(selectedRegistration._id, statusToUpdate);
      
      // Mettre à jour l'état local
      setRegistrations(prevRegistrations => 
        prevRegistrations.map(reg => 
          reg._id === selectedRegistration._id 
            ? { ...reg, status: statusToUpdate } 
            : reg
        )
      );
      
      handleCloseRegistrationDialog();
    } catch (error) {
      console.error('Error updating registration status:', error);
    }
  };

  const markContactAsRead = async (contactId) => {
    try {
      await api.markContactAsRead(contactId);
      
      // Mettre à jour l'état local
      setContacts(prevContacts => 
        prevContacts.map(contact => 
          contact._id === contactId 
            ? { ...contact, isRead: true } 
            : contact
        )
      );
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await api.deleteContact(contactId);
      
      // Mettre à jour la liste des contacts en supprimant celui qui vient d'être supprimé
      setContacts(contacts.filter(contact => contact._id !== contactId));
      
      // Fermer la boîte de dialogue si elle est ouverte
      if (openContactDialog) {
        handleCloseContactDialog();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const deleteRegistration = async (registrationId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette inscription ? Cette action est irréversible.')) {
      try {
        await api.deleteRegistration(registrationId);
        
        // Mettre à jour la liste des inscriptions en supprimant celle qui vient d'être supprimée
        setRegistrations(registrations.filter(registration => registration._id !== registrationId));
        
        // Fermer la boîte de dialogue si elle est ouverte
        if (openRegistrationDialog && selectedRegistration && selectedRegistration._id === registrationId) {
          handleCloseRegistrationDialog();
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'inscription:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success.main';
      case 'rejected':
        return 'error.main';
      default:
        return 'warning.main';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle color="success" />;
      case 'rejected':
        return <Cancel color="error" />;
      default:
        return null;
    }
  };

  const unreadContactsCount = contacts.filter(contact => !contact.isRead).length;

  // Pagination logic for registrations
  const totalRegistrationsPages = Math.ceil(registrations.length / itemsPerPage);
  const paginatedRegistrations = registrations.slice(
    (registrationsPage - 1) * itemsPerPage,
    registrationsPage * itemsPerPage
  );

  // Pagination logic for contacts
  const totalContactsPages = Math.ceil(contacts.length / itemsPerPage);
  const paginatedContacts = contacts.slice(
    (contactsPage - 1) * itemsPerPage,
    contactsPage * itemsPerPage
  );

  const navigateToSection = (section) => {
    navigate(`/admin/${section}`);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: '#f5f5f5'
    }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Tableau de bord administrateur
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Déconnexion
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Grid container spacing={3}>
          {/* Boutons de navigation */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<People />}
                onClick={() => navigateToSection('users')}
                sx={{ flex: 1 }}
              >
                Utilisateurs
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<BarChart />}
                onClick={() => navigateToSection('stats')}
                sx={{ flex: 1 }}
              >
                Statistiques
              </Button>
               <Button 
                variant="contained" 
                color="primary" 
                startIcon={<Event />}
                onClick={() => navigateToSection('events')}
                sx={{ flex: 1 }}
              >
                Événements
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Inscriptions" />
                  <Tab 
                    label={
                      <Badge badgeContent={unreadContactsCount} color="error">
                        Contacts
                      </Badge>
                    } 
                  />
                </Tabs>
              </Box>

              {tabValue === 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Inscriptions récentes</Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<FileDownload />}
                      onClick={handleExport}
                    >
                      Exporter
                    </Button>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Nom</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Téléphone</TableCell>
                            <TableCell>N° CIN</TableCell>
                            <TableCell>Localité</TableCell>
                            <TableCell>Niveau Scolaire</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Pièces jointes</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedRegistrations.length > 0 ? (
                            paginatedRegistrations.map((registration) => (
                              <TableRow key={registration._id}>
                                <TableCell>{registration.firstName} {registration.lastName}</TableCell>
                                <TableCell>{registration.email}</TableCell>
                                <TableCell>{registration.phone}</TableCell>
                                <TableCell>{registration.cinNumber}</TableCell>
                                <TableCell>{registration.locality}</TableCell>
                                <TableCell>{registration.education}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {getStatusIcon(registration.status)}
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                      {registration.status === 'pending' ? 'En Attente' : 
                                       registration.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {registration.cinFile && (
                                      <IconButton 
                                        size="small" 
                                        color="primary" 
                                        title="Télécharger CIN"
                                        onClick={() => handleDownloadFile(registration.cinFile, 'CIN')}
                                      >
                                        <BadgeIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    {registration.ribFile && (
                                      <IconButton 
                                        size="small" 
                                        color="primary" 
                                        title="Télécharger RIB"
                                        onClick={() => handleDownloadFile(registration.ribFile, 'RIB')}
                                      >
                                        <AccountBalanceWallet fontSize="small" />
                                      </IconButton>
                                    )}
                                    {registration.certificateFile && (
                                      <IconButton 
                                        size="small" 
                                        color="primary" 
                                        title="Télécharger Certificat"
                                        onClick={() => handleDownloadFile(registration.certificateFile, 'Certificat')}
                                      >
                                        <School fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleViewRegistration(registration)}
                                      title="Voir les détails"
                                    >
                                      <Visibility />
                                    </IconButton>
                                    <IconButton
                                      color="error"
                                      onClick={() => deleteRegistration(registration._id)}
                                      title="Supprimer l'inscription"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                Aucune inscription trouvée
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {registrations.length > 0 && (
                    <Pagination
                      currentPage={registrationsPage}
                      totalPages={totalRegistrationsPages}
                      onPageChange={setRegistrationsPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={registrations.length}
                    />
                  )}
                </>
              )}

              {tabValue === 1 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>Messages de contact</Typography>
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Nom</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Sujet</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedContacts.length > 0 ? (
                            paginatedContacts.map((contact) => (
                              <TableRow 
                                key={contact._id}
                                sx={{ 
                                  bgcolor: contact.isRead ? 'inherit' : 'rgba(25, 118, 210, 0.08)'
                                }}
                              >
                                <TableCell>{contact.name}</TableCell>
                                <TableCell>{contact.email}</TableCell>
                                <TableCell>{contact.subject}</TableCell>
                                <TableCell>
                                  {new Date(contact.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <IconButton 
                                    color="primary" 
                                    onClick={() => handleViewContact(contact)}
                                  >
                                    <Visibility />
                                  </IconButton>
                                  {!contact.isRead && (
                                    <Badge color="error" variant="dot" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                Aucun message trouvé
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {contacts.length > 0 && (
                    <Pagination
                      currentPage={contactsPage}
                      totalPages={totalContactsPages}
                      onPageChange={setContactsPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={contacts.length}
                    />
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Dialogue de détail d'inscription */}
        <Dialog open={openRegistrationDialog} onClose={handleCloseRegistrationDialog} maxWidth="md" fullWidth>
          {selectedRegistration && (
            <>
              <DialogTitle>
                Détails de l'inscription de {selectedRegistration.firstName} {selectedRegistration.lastName}
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Email:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.email}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Téléphone:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.phone}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">N° CIN:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.cinNumber}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Date de naissance:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.birthDate}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Comment a-t-il entendu parler de nous:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.hearAboutUs || 'Non spécifié'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Adresse:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.address}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Localité:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.locality}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Niveau Scolaire:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedRegistration.education}</Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Date d'inscription:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(selectedRegistration.createdAt).toLocaleDateString()}
                    </Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold">Conditions acceptées:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedRegistration.termsAccepted ? 'Oui' : 'Non'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Motivation:</Typography>
                    <Typography variant="body1" paragraph>{selectedRegistration.motivation}</Typography>
                  </Grid>
                  
                  {/* Pièces jointes */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Pièces jointes:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                      {selectedRegistration.cinFile && (
                        <Button
                          variant="outlined"
                          startIcon={<BadgeIcon />}
                          onClick={() => handleDownloadFile(selectedRegistration.cinFile, 'CIN')}
                        >
                          CIN
                        </Button>
                      )}
                      {selectedRegistration.ribFile && (
                        <Button
                          variant="outlined"
                          startIcon={<AccountBalanceWallet />}
                          onClick={() => handleDownloadFile(selectedRegistration.ribFile, 'RIB')}
                        >
                          RIB
                        </Button>
                      )}
                      {selectedRegistration.certificateFile && (
                        <Button
                          variant="outlined"
                          startIcon={<School />}
                          onClick={() => handleDownloadFile(selectedRegistration.certificateFile, 'Certificat')}
                        >
                          Certificat Scolaire
                        </Button>
                      )}
                      {!selectedRegistration.cinFile && !selectedRegistration.ribFile && !selectedRegistration.certificateFile && (
                        <Typography variant="body2" color="text.secondary">Aucune pièce jointe</Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="status-select-label">Statut</InputLabel>
                      <Select
                        labelId="status-select-label"
                        value={statusToUpdate}
                        label="Statut"
                        onChange={handleStatusChange}
                      >
                        <MenuItem value="pending">En attente</MenuItem>
                        <MenuItem value="approved">Approuvé</MenuItem>
                        <MenuItem value="rejected">Rejeté</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseRegistrationDialog}>Annuler</Button>
                <Button 
                  onClick={() => deleteRegistration(selectedRegistration._id)}
                  variant="contained"
                  color="error"
                  sx={{ mr: 1 }}
                >
                  Supprimer
                </Button>
                <Button 
                  onClick={updateRegistrationStatus} 
                  variant="contained" 
                  color="primary"
                  disabled={statusToUpdate === selectedRegistration.status}
                >
                  Mettre à jour le statut
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Dialogue de détail de contact */}
        <Dialog open={openContactDialog} onClose={handleCloseContactDialog} maxWidth="md" fullWidth>
          {selectedContact && (
            <>
              <DialogTitle>
                Message de {selectedContact.name}
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Email:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedContact.email}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold">Date:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(selectedContact.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Sujet:</Typography>
                    <Typography variant="body1" gutterBottom>{selectedContact.subject}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Message:</Typography>
                    <Typography variant="body1" paragraph>{selectedContact.message}</Typography>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseContactDialog}>Fermer</Button>
                <Button 
                  onClick={() => deleteContact(selectedContact._id)} 
                  variant="contained" 
                  color="error"
                >
                  Supprimer
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export { AdminDashboard };
